import Image from "next/image";
import { Reveal } from "@/components/ui/Reveal";
import { DOCTORS } from "@/lib/site-data";

const DOT_COLOR = ["bg-brand-orange", "bg-brand-peach", "bg-brand-green"];
const PANEL_BG = ["bg-brand-black", "bg-gradient-to-br from-brand-black to-brand-brown"];

function BadgeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-4.5 w-4.5 text-white">
      <path
        d="M12 2 3 7v10l9 5 9-5V7l-9-5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M12 12v9M12 12 3.5 7.2M12 12l8.5-4.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? "")).toUpperCase();
}

export function Doctors({
  eyebrow,
  title,
  highlightText,
  items,
}: {
  eyebrow?: string;
  title?: string;
  highlightText?: string;
  items?: Array<{
    name: string;
    specialty?: string;
    title: string;
    credentials: string;
    bio: string;
    image: string;
  }>;
}) {
  const finalEyebrow = eyebrow || "Meet The Team";
  const finalTitle = title || "The Doctors Behind";
  const finalHighlightText = highlightText !== undefined ? highlightText : "Your Results";

  const staticConverted = DOCTORS.map((d, i) => {
    const categories = ["Hair Transplant Specialist", "Plastic & Cosmetic Surgeon"];
    return {
      name: d.name,
      specialty: categories[i % categories.length],
      title: d.title,
      credentials: d.credentials,
      bio: d.bio,
      image: d.image || "",
    };
  });

  const finalItems = items && items.length > 0 ? items : staticConverted;

  return (
    <section id="doctors" className="bg-white py-24">
      <div className="w-full px-6 md:px-12 xl:px-[170px]">
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <span className="font-accent text-xs uppercase text-brand-orange-deep">
            {finalEyebrow}
          </span>
          <h2 className="mt-4 text-4xl font-semibold leading-[1.1] text-brand-black sm:text-5xl">
            {finalTitle}{" "}
            {finalHighlightText && (
              <span className="text-brand-orange">{finalHighlightText}</span>
            )}
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2">
          {finalItems.map((doctor, i) => {
            const credentials = doctor.credentials
              .split(/[·,]+/)
              .map((s) => s.trim())
              .filter(Boolean);
            const reversed = i % 2 === 1;

            return (
              <Reveal key={doctor.name} delay={i * 0.1} y={32}>
                <div
                  className={`relative flex h-full min-h-[300px] flex-col overflow-hidden rounded-3xl sm:flex-row ${PANEL_BG[i % PANEL_BG.length]} ${
                    reversed ? "sm:flex-row-reverse" : ""
                  }`}
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 text-white opacity-[0.06]"
                    style={{
                      backgroundImage: "radial-gradient(currentColor 1px, transparent 1px)",
                      backgroundSize: "20px 20px",
                    }}
                  />

                  {doctor.image ? (
                    <div className="relative h-72 w-full shrink-0 sm:h-auto sm:w-[46%]">
                      <Image
                        src={doctor.image}
                        alt={doctor.name}
                        fill
                        sizes="(min-width: 1024px) 30vw, 45vw"
                        className="object-cover object-top"
                      />
                      <div
                        aria-hidden
                        className={`pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent via-[45%] to-brand-black/95 ${
                          reversed ? "sm:bg-gradient-to-l" : "sm:bg-gradient-to-r"
                        }`}
                      />
                    </div>
                  ) : (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute -right-2 bottom-0 select-none font-heading text-[6rem] font-semibold leading-none text-white/[0.08]"
                    >
                      {getInitials(doctor.name)}
                    </span>
                  )}

                  <div className="relative flex flex-1 flex-col justify-center gap-3 p-6 sm:p-7">
                    <div className="relative">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-orange">
                          <BadgeIcon />
                        </span>
                        <span className="font-accent text-xs uppercase text-brand-peach">
                          {doctor.specialty || "Specialist"}
                        </span>
                      </div>

                      <h3 className="mt-3 text-xl font-semibold text-white sm:text-2xl">
                        {doctor.name}
                      </h3>
                      <p className="mt-1 text-sm font-semibold text-brand-orange">{doctor.title}</p>

                      {credentials.length > 0 && (
                        <div className="mt-2.5 flex flex-wrap gap-1.5">
                          {credentials.map((c, ci) => (
                            <span
                              key={c}
                              className="flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm"
                            >
                              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_COLOR[ci % DOT_COLOR.length]}`} />
                              {c}
                            </span>
                          ))}
                        </div>
                      )}

                      <div
                        className="mt-3 text-sm text-white/70 leading-snug [&>p]:mt-2"
                        dangerouslySetInnerHTML={{ __html: doctor.bio }}
                      />

                      <span className="mt-3 block h-[3px] w-7 rounded-full bg-brand-orange" />
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
